/**
 * Sesión compartida entre nukliapp.com (landing) y mail.nukliapp.com (app web).
 *
 * Son orígenes distintos, así que no comparten localStorage. Lo que sí
 * comparten es el dominio registrable: una cookie con `Domain=.nukliapp.com` la
 * ven los dos. Dentro va SOLO el refresh token, y esa cookie es la fuente de
 * verdad: el servidor ROTA el refresh en cada uso (`/auth/refresh` revoca el
 * anterior), de modo que solo hay un token vivo en toda la cadena. Quien vaya a
 * refrescar debe leer siempre el último valor de aquí, o se encontraría con una
 * sesión ya revocada.
 *
 * El access token no viaja en la cookie: cada origen guarda el suyo en
 * localStorage y lo renueva por su cuenta con el refresh compartido.
 *
 * En local no se pone `Domain`: la cookie queda host-only, pero como las
 * cookies ignoran el puerto, landing (:8080) y app web (:5173) la comparten
 * igual mientras ambas corran en localhost.
 *
 * El mismo contrato está implementado en la app web: ver
 * m-ai-l/src/renderer/lib/sso.ts. Cualquier cambio de nombre, formato o
 * atributos hay que hacerlo en los dos sitios.
 */

/** Nombre de la cookie compartida. Debe coincidir con el de la app web. */
const COOKIE = 'nukli_sso';

/**
 * Marca de "aquí se ha cerrado la sesión". Sin ella el logout no se propaga:
 * el otro origen, al arrancar y no encontrar la cookie, la sembraría otra vez
 * con su copia local del refresh token y seguiría dentro con su access token
 * hasta que caducara. Dura lo que un access token (15 min): pasado ese plazo
 * ningún origen puede seguir vivo por su cuenta.
 */
const OUT_COOKIE = 'nukli_sso_out';
const OUT_MAX_AGE = 15 * 60;

/** 30 días: lo mismo que REFRESH_TTL_MS en la API. */
const MAX_AGE = 30 * 24 * 60 * 60;

/** Dominio registrable donde viven landing y app. */
const ROOT_DOMAIN = 'nukliapp.com';

function canUseCookies() {
  return typeof document !== 'undefined';
}

/** `; Domain=.nukliapp.com` en producción; nada en local (host-only). */
function domainAttr() {
  const host = window.location.hostname;
  return host === ROOT_DOMAIN || host.endsWith('.' + ROOT_DOMAIN)
    ? '; Domain=.' + ROOT_DOMAIN
    : '';
}

function secureAttr() {
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

/** Refresh token compartido, o null si no hay sesión en ningún origen. */
export function readSharedRefreshToken() {
  if (!canUseCookies()) return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'));
  if (!match) return null;
  const value = decodeURIComponent(match[1] || '');
  return value || null;
}

/** Publica el refresh token recién emitido para el otro origen. */
export function writeSharedRefreshToken(token) {
  if (!canUseCookies() || !token) return;
  document.cookie =
    COOKIE + '=' + encodeURIComponent(token) +
    '; Path=/; Max-Age=' + MAX_AGE + '; SameSite=Lax' +
    domainAttr() + secureAttr();
  // Cualquier sesión nueva cancela un cierre anterior.
  clearCookie(OUT_COOKIE);
}

/** Borra la cookie compartida (logout o refresh token ya inválido). */
export function clearSharedRefreshToken() {
  clearCookie(COOKIE);
}

/** Anuncia al otro origen que aquí se ha cerrado la sesión. */
export function markSharedSignOut() {
  if (!canUseCookies()) return;
  document.cookie =
    OUT_COOKIE + '=1; Path=/; Max-Age=' + OUT_MAX_AGE + '; SameSite=Lax' +
    domainAttr() + secureAttr();
}

/** ¿Se ha cerrado la sesión en el otro origen hace poco? */
export function isSharedSignOut() {
  if (!canUseCookies()) return false;
  return new RegExp('(?:^|; )' + OUT_COOKIE + '=').test(document.cookie);
}

function clearCookie(name) {
  if (!canUseCookies()) return;
  const tail = '=; Path=/; Max-Age=0; SameSite=Lax' + secureAttr();
  // Con y sin Domain: si quedara una versión host-only de una sesión anterior,
  // borrar solo la de dominio dejaría la vieja tapando el hueco.
  document.cookie = name + tail + domainAttr();
  document.cookie = name + tail;
}
